import type { ReactNode } from "react"
import { Label, RadioGroup, RadioGroupItem } from "@ui/shad"
import { cn } from "@ui/utils"

interface Option {
  value: string
  label: string
  description?: string
}

interface RadioInputProps {
  id: string
  value?: string
  onChange?: (value: string) => void
  options: Option[]
  label?: string
  error?: boolean
  disabled?: boolean
  renderOptionContent?: (option: Option) => ReactNode
}

export function RadioInput({
  id,
  value,
  onChange,
  options,
  label,
  error,
  disabled = false,
  renderOptionContent,
}: RadioInputProps) {
  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id} className={cn("block", error && "text-destructive")}>
          {label}
        </Label>
      )}
      <RadioGroup value={value} onValueChange={onChange} disabled={disabled} className="space-y-2">
        {options.map((option) => {
          const selected = value === option.value

          return (
            <div
              key={option.value}
              className={cn(
                "cursor-pointer rounded-md border p-3 transition-colors",
                selected ? "bg-muted" : "hover:bg-muted/50",
                disabled && "cursor-not-allowed opacity-50"
              )}
              onClick={() => !disabled && onChange?.(option.value)}
            >
              <div className="flex items-start gap-3">
                <RadioGroupItem value={option.value} id={`${id}-${option.value}`} className="mt-0.5" />
                <Label htmlFor={`${id}-${option.value}`} className="min-w-0 flex-1 cursor-pointer space-y-1">
                  <span className="block font-medium">{option.label}</span>
                  {option.description && (
                    <span className="text-muted-foreground block text-xs leading-relaxed">{option.description}</span>
                  )}
                </Label>
              </div>
              {selected && renderOptionContent?.(option)}
            </div>
          )
        })}
      </RadioGroup>
    </div>
  )
}
